import type { EventActivity, EventAttendance, EventAttendee, EventHistory, EventRegistration } from './types';

export type AttendanceFilter = 'all' | 'Registered' | 'Waitlisted' | 'Attended' | 'NoShow' | 'Cancelled';

export interface AttendanceCounts { registered: number; waitlisted: number; attended: number; noShows: number; cancelled: number }

export interface AttendanceRow { person: EventAttendee; registration?: EventRegistration; attendance?: EventAttendance }

// Mirrors the server: anyone on the team takes back their own check-in; only the lead or an admin undoes a
// no-show or another rep's check-in. A passive sighting or an unsynced check-in has nothing to undo yet.
export function canUndoAttendance(visit: EventAttendance | undefined, canManage: boolean, actorId: string | undefined): boolean {
  if (!visit || visit.localPending || visit.source === 'Passive') return false;
  return canManage || (visit.status === 'Attended' && !!actorId && visit.representativeId === actorId);
}

export const undoAttendanceLabel = (visit: Pick<EventAttendance, 'status'>) => visit.status === 'NoShow' ? 'Undo no-show' : 'Undo check-in';

export function attendanceCounts(activityId: string, registrations: EventRegistration[] = [], attendance: EventAttendance[] = []): AttendanceCounts {
  const reg = (status: EventRegistration['status']) => registrations.filter((r) => r.activityId === activityId && !r.localPending && r.status === status).length;
  const visit = (status: EventAttendance['status']) => attendance.filter((r) => r.activityId === activityId && !r.localPending && r.status === status).length;
  return { registered: reg('Registered'), waitlisted: reg('Waitlisted'), attended: visit('Attended'), noShows: visit('NoShow'), cancelled: reg('Cancelled') };
}

export function attendanceRows(activityId: string, people: EventAttendee[] = [], registrations: EventRegistration[] = [], attendance: EventAttendance[] = []): AttendanceRow[] {
  const firstByAttendee = <T extends { attendeeId: string; activityId: string }>(records: T[]) => {
    const found = new Map<string, T>();
    for (const record of records) if (record.activityId === activityId && !found.has(record.attendeeId)) found.set(record.attendeeId, record);
    return found;
  };
  const registrationFor = firstByAttendee(registrations);
  const attendanceFor = firstByAttendee(attendance);
  return people.filter((person) => !person.isPrivate).map((person) => ({ person, registration: registrationFor.get(person.id), attendance: attendanceFor.get(person.id) }));
}

export function isRemovedGuest(row: Pick<AttendanceRow, 'registration'>): boolean {
  return row.registration?.status === 'Cancelled' && row.registration.source === 'Removed';
}

// A guest the lead removed stays findable under Cancelled but leaves every other view.
export function matchesAttendanceFilter(row: AttendanceRow, filter: AttendanceFilter): boolean {
  if (isRemovedGuest(row) && filter !== 'Cancelled') return false;
  return filter === 'all' || row.registration?.status === filter || row.attendance?.status === filter;
}

export function canRemoveGuest(activity: Pick<EventActivity, 'type'>, row: Pick<AttendanceRow, 'registration'>, canManage: boolean): boolean {
  return canManage && activity.type === 'InvitationEvent' && !!row.registration && !row.registration.localPending && !isRemovedGuest(row);
}

// Mirrors EventService.RemoveGuestAsync and the delivery worker: an active registration queues the usual
// cancellation email, which only goes out while event email is on.
export function guestRemovalNotice(status: EventRegistration['status'], emailEnabled: boolean, waitlisted: number): string[] {
  if (status === 'Cancelled') return ['Their registration is already cancelled, so no email goes out.'];
  const lines = [emailEnabled
    ? 'Their registration is cancelled and they get the cancellation email.'
    : 'Their registration is cancelled. Event email is off, so the cancellation email waits in the queue and only goes out if email is turned on.'];
  if (status === 'Registered' && waitlisted > 0) lines.push('If this frees a place, people on the waitlist move up in order and are notified the usual way.');
  return lines;
}

export function matchesAttendanceSearch(row: AttendanceRow, search: string): boolean {
  return `${row.person.name} ${row.person.email} ${row.person.company}`.toLowerCase().includes(search.trim().toLowerCase());
}

export const DEFAULT_ATTENDANCE_FILTER: AttendanceFilter = 'Registered';

// The roster spans the whole event, so someone with no registration or attendance for this activity
// only shows up when the lead searches for them (to check in a walk-in or register them at the door).
export function onActivityList(row: AttendanceRow): boolean {
  return !!row.registration || !!row.attendance;
}

export function visibleAttendanceRows(rows: AttendanceRow[], filter: AttendanceFilter, search: string): AttendanceRow[] {
  if (!search.trim()) return rows.filter((row) => onActivityList(row) && matchesAttendanceFilter(row, filter));
  return rows.filter((row) => matchesAttendanceSearch(row, search) && (!onActivityList(row) || matchesAttendanceFilter(row, filter)));
}

export function attendanceFilterCount(rows: AttendanceRow[], filter: AttendanceFilter): number {
  return rows.filter((row) => onActivityList(row) && matchesAttendanceFilter(row, filter)).length;
}

const confirmedCheckIn = (row: AttendanceRow) => row.attendance?.status === 'Attended' && !row.attendance.localPending && row.attendance.source !== 'Passive';

// Checked in without a registration. Display only: the server stores it as an attendance record with no registration.
export function isWalkIn(row: AttendanceRow): boolean {
  return !row.registration && confirmedCheckIn(row);
}

export interface DoorProgress { checkedIn: number; registered: number; walkIns: number }

export function doorProgress(rows: AttendanceRow[]): DoorProgress {
  const registered = rows.filter((row) => row.registration?.status === 'Registered' && !row.registration.localPending);
  return { checkedIn: registered.filter(confirmedCheckIn).length, registered: registered.length, walkIns: rows.filter(isWalkIn).length };
}

// Registered people with no attendance record at all. A passive sighting counts as a record: someone saw them there.
export function remainingNoShows(rows: AttendanceRow[]): AttendanceRow[] {
  return rows.filter((row) => row.registration?.status === 'Registered' && !row.registration.localPending && !row.person.localPending && !row.attendance);
}

// Mirrors EventService.ApplyRegistration: a cancelled Registered place promotes waitlisted people in order while
// places are free (none if the activity stays over capacity); both emails only go out while event email is on.
export function cancellationNotice(status: EventRegistration['status'], emailEnabled: boolean, waitlisted: number): string[] {
  const lines = [emailEnabled
    ? 'They get the cancellation email.'
    : 'Event email is off, so the cancellation email waits in the queue and only goes out if email is turned on.'];
  if (status === 'Registered' && waitlisted > 0) lines.push(emailEnabled
    ? 'If this frees a place, people on the waitlist are registered automatically, in order, and get the waitlist promotion email.'
    : 'If this frees a place, people on the waitlist are registered automatically, in order. Their promotion emails also wait while event email is off.');
  return lines;
}

// Capacity counts confirmed registrations only; a full activity turns a normal register into a waitlist entry server-side.
export function activityIsFull(activity: Pick<EventActivity, 'capacity'>, registered: number): boolean {
  return activity.capacity !== null && registered >= activity.capacity;
}

export interface ReminderStatus { label: string; badge: string | null; detail?: string }

// A pending reminder is moot once EventDeliveryProcessor.ShouldSuppress would drop it: the session started or the
// person is no longer registered. Showing its next attempt would promise an email that never goes out.
export function reminderStatus(attendeeId: string, activity: Pick<EventActivity, 'id' | 'reminderMinutes' | 'startsAt'>, history: EventHistory | undefined, historyError: boolean,
  timeZone?: string | null, registrationStatus?: EventRegistration['status'], now = Date.now()): ReminderStatus {
  if (historyError) return { label: 'Status unavailable', badge: null };
  const reminder = history?.deliveries
    .filter((delivery) => delivery.attendeeId === attendeeId && delivery.activityId === activity.id && delivery.action === 'SessionReminder')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  if (!reminder) return { label: activity.reminderMinutes ? 'No reminder queued' : 'Not configured', badge: null };
  if (reminder.status === 'Pending') {
    if (registrationStatus !== 'Registered') return { label: '—', badge: null };
    if (new Date(activity.startsAt).getTime() <= now) return { label: 'Not sent · session started', badge: null };
  }
  const label = reminder.status === 'Completed' ? 'Accepted by email provider' : reminder.status;
  const detail = reminder.nextAttemptAt && reminder.status === 'Pending' ? `Next attempt ${formatInEventZone(reminder.nextAttemptAt, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZoneName: 'short' } : {}) }, timeZone)}` : undefined;
  return { label, badge: `event${reminder.status.toLowerCase()}`, ...(detail ? { detail } : {}) };
}

const SOURCE_LABELS: Record<string, string> = {
  Manual: 'Manual', Badge: 'Badge scan', Booth: 'Booth', Offline: 'Offline capture', GuestImport: 'Guest import', Passive: 'Passive presence', Removed: 'Removed from guest list',
};

export function sourceLabel(source: string | null | undefined): string {
  return source ? SOURCE_LABELS[source] ?? source : '';
}

export function formatCheckIn(value: string | null | undefined, timeZone?: string | null): string {
  if (!value) return '';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  const time = formatInEventZone(value, { hour: 'numeric', minute: '2-digit' }, timeZone);
  const day = (d: Date) => formatInEventZone(d.toISOString(), { year: 'numeric', month: 'numeric', day: 'numeric' }, timeZone);
  return day(at) === day(new Date()) ? time : `${formatInEventZone(value, { month: 'short', day: 'numeric' }, timeZone)} ${time}`;
}

// Activity times read in the event's own zone so a rep's travelling phone doesn't shift the schedule.
export function formatInEventZone(value: string, options: Intl.DateTimeFormatOptions, timeZone: string | null | undefined): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(undefined, timeZone ? { ...options, timeZone } : options);
  } catch {
    return date.toLocaleString(undefined, options);
  }
}

export function activityWhen(activity: Pick<EventActivity, 'startsAt'>, timeZone?: string | null): string {
  return formatInEventZone(activity.startsAt, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZoneName: 'short' } : {}) }, timeZone);
}

function zoneOffset(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((item) => item.type === type)?.value);
  return Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second')) - Math.floor(at / 1000) * 1000;
}

function browserInputValue(at: number): string {
  return new Date(at - new Date(at).getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

// datetime-local fields hold wall-clock time at the venue, so they convert through the event's zone; the browser's zone is only the fallback.
export function toEventInputValue(value: string | undefined, timeZone: string | null | undefined): string {
  const at = value ? new Date(value).getTime() : Number.NaN;
  if (Number.isNaN(at)) return '';
  if (!timeZone) return browserInputValue(at);
  try {
    return new Date(at + zoneOffset(at, timeZone)).toISOString().slice(0, 16);
  } catch {
    return browserInputValue(at);
  }
}

export function fromEventInputValue(value: string, timeZone: string | null | undefined): string {
  if (!timeZone) return new Date(value).toISOString();
  try {
    const wall = new Date(`${value}Z`).getTime();
    // Second pass settles the offset when the first guess lands on the other side of a daylight-saving change.
    const guess = wall - zoneOffset(wall, timeZone);
    return new Date(wall - zoneOffset(guess, timeZone)).toISOString();
  } catch {
    return new Date(value).toISOString();
  }
}
